import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

// Helper function to format patient name based on location
const formatPatientName = (patient) => {
  if (!patient) return '';

  // For miracletouch location, show as "Last Name, First Name"
  if (patient.location === 'miracletouch') {
    const lastName = patient.last_name || '';
    const firstName = patient.first_name || '';
    const middleInitial = patient.middle_initial ? ` ${patient.middle_initial}.` : '';

    if (lastName && firstName) {
      return `${lastName}, ${firstName}${middleInitial}`;
    }
  }

  // For all other locations, use the standard name field
  return patient.name || '';
};

export const PatientCard = ({ patient, selected, onClick, isDeleting }) => {
  // Parse a date value and convert to the given IANA timezone.
  // Treat strings without an explicit offset/Z as UTC to avoid double-shifting.
  const parseToTimezone = (value, targetTimezone) => {
    if (!value) return null;
    const stringValue = typeof value === 'string' ? value : String(value);
    const hasExplicitOffset = /[zZ]|[+\-]\d{2}:?\d{2}$/.test(stringValue);
    const base = hasExplicitOffset ? dayjs(stringValue) : dayjs.utc(stringValue);
    const zoned = base.tz(targetTimezone);
    return zoned.isValid() ? zoned : null;
  };

  // Get the location's timezone
  const getLocationTimezone = (location) => {
    const loc = (location || 'evergreen').toLowerCase().trim();
    const timezoneMap = {
      'evergreen': 'America/Los_Angeles',
      'siliconbeach': 'America/Los_Angeles',
      'weho': 'America/Los_Angeles',
      'brentwood': 'America/Los_Angeles',
      'skindrmbw': 'America/Los_Angeles',
      'skindrmweho': 'America/Los_Angeles',
      'dtla': 'America/Los_Angeles',
      'oceanpark': 'America/Los_Angeles',
      'confdemo': 'America/New_York',
      'cedarhills': 'America/Los_Angeles',
      'hillsboro': 'America/Los_Angeles',
      'hallandale': 'America/New_York',  // Eastern Time
      'speer': 'America/Denver',
      'wylie': 'America/Chicago', // Central Time (Dallas)
      'iec': 'America/Chicago',
      'lincoln': 'America/Chicago',
      'wicker': 'America/Chicago',
      'logan': 'America/Chicago',
      '2418': 'America/Chicago',
      'diversey': 'America/Chicago',
      'brockton': 'America/New_York',
      'sonoma': 'America/Los_Angeles',
      'napa': 'America/Los_Angeles',
      'benicia': 'America/Los_Angeles',
      'petaluma': 'America/Los_Angeles',
      'modernmdfulton': 'America/New_York',
      'modernmdflatbush': 'America/New_York',
      'modernmdfreshpond': 'America/New_York',
      'modernmdgraham': 'America/New_York',
      'modernmdjh': 'America/New_York',
      'modernmdjamaica': 'America/New_York',
      'modernmdknickerbrocker': 'America/New_York',
      'modernmdlefferts': 'America/New_York',
      'modernmdlibertyave': 'America/New_York',
      'modernmdnorthutica': 'America/New_York',
      'modernmdsunsetpark': 'America/New_York',
      'modernmdutica': 'America/New_York',
      'pulsemd': 'America/New_York',
      'valvista': 'America/Phoenix',  // Arizona Time
      'queencreek': 'America/Phoenix',  // Arizona Time
      'higley': 'America/Phoenix',  // Arizona Time
      'gilbert': 'America/Phoenix'  // Arizona Time
    };
    return timezoneMap[loc] || 'America/Los_Angeles';
  };

  // Attempt to parse displayTime if it exists, else use created_at, else fallback
  const rawDate = patient.displayTime || patient.created_at;
  const timezone = getLocationTimezone(patient.location);
  const dateObj = parseToTimezone(rawDate, timezone);
  const isValidDate = !!dateObj;
  
  const displayDate = isValidDate ? dateObj.format("MM/DD/YYYY") : "";
  const displayTime = isValidDate ? dateObj.format("h:mm A") : "";
  
  // Parse questionnaire completion date/time for scheduled patients
  const questionnaireObj = patient.questionnaire_completed_at 
    ? parseToTimezone(patient.questionnaire_completed_at, timezone)
    : null;
  const questionnaireTime = questionnaireObj && questionnaireObj.isValid() 
    ? questionnaireObj.format("h:mm A") 
    : "";
  const questionnaireDate = questionnaireObj && questionnaireObj.isValid() 
    ? questionnaireObj.format("MM/DD/YYYY") 
    : "";
    
  // Check for different types of scheduling information
  const isScheduled = patient.isScheduled;
  const hasQuestionnaire = patient.questionnaire_completed_at && questionnaireObj?.isValid();
  const hasAppointment = patient.appointment_time && patient.appointment_date;

  return (
    <div
      onClick={onClick}
      className={`p-4 border rounded-lg mb-3 cursor-pointer hover:bg-gray-100 relative
        ${selected ? "border-teal-500 bg-teal-50" : "border-gray-200"}
        ${isDeleting ? "opacity-70" : ""}
      `}
    >
      {isDeleting && (
        <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-60 rounded-lg z-10">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-teal-500"></div>
        </div>
      )}
      
      <div className="flex justify-between items-start">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="font-medium">{formatPatientName(patient)}</h3>
            
            {/* Show scheduling status badge */}
            {isScheduled && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800">
                Scheduled
              </span>
            )}
          </div>
          
          <div className="text-sm text-gray-500 flex flex-wrap items-center gap-2">
            {/* Show displayTime if valid, else show nothing */}
            {displayTime && <span>{displayTime}</span>}
            {displayDate && <span>{displayDate}</span>}
            <span>
              {patient.dob && patient.dob.includes('/')
                ? `DOB: ${patient.dob}`
                : patient.dob
                  ? `Age: ${patient.dob}${patient.dob.toString().includes('Y') ? '' : ' Y'}`
                  : ''}
            </span>
          </div>
          
          {/* Show appointment details if available */}
          {hasAppointment && (
            <div className="mt-2 flex items-center">
              <div className="inline-flex items-center px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs font-medium">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="mr-1"
                >
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                  <line x1="16" y1="2" x2="16" y2="6"></line>
                  <line x1="8" y1="2" x2="8" y2="6"></line>
                  <line x1="3" y1="10" x2="21" y2="10"></line>
                </svg>
                Appointment: {patient.appointment_time} {patient.appointment_date}
              </div>
            </div>
          )}
          
          {/* Show questionnaire completion time if available */}
          {isScheduled && hasQuestionnaire && (
            <div className="mt-2 flex items-center">
              <div className="inline-flex items-center px-2 py-1 bg-green-50 text-green-700 rounded text-xs font-medium">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="mr-1"
                >
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                  <polyline points="22 4 12 14.01 9 11.01"></polyline>
                </svg>
                Questionnaire completed: {questionnaireTime} {questionnaireDate}
              </div>
            </div>
          )}
          
          {/* Show scheduled but no questionnaire completed status */}
          {isScheduled && !hasQuestionnaire && (
            <div className="mt-2 flex items-center">
              <div className="inline-flex items-center px-2 py-1 bg-yellow-50 text-yellow-700 rounded text-xs font-medium">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="mr-1"
                >
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="8" x2="12" y2="12"></line>
                  <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
                Awaiting questionnaire completion
              </div>
            </div>
          )}
        </div>
        <div className="text-sm text-gray-600">{patient.reason}</div>
      </div>
    </div>
  );
};